import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loading from 'common/chart/loading';
import ChartWrapper from 'common/chart';
import FilterCards from 'common/edge-filter-cards';
import { selectHasAiContext, selectIsLoading } from 'store/graph/selectors';
import styles from './home.module.css';
import FeedbackModel from 'common/feeback_modal';
import useSearchNotification from 'utils/useSearchNotification';
import {
  selectShowFeedbackModal,
  selectShowIndividualFilters,
} from 'store/core/selectors';
import { Sidebar } from 'common/sidebar';
import ContextFiltersModal from 'common/context-filters-modal';
import SelectedDetailNode from './selectedDetailNode';
import SelectedEdge from './selectedEdge';
import CommandBar from 'common/command-bar';
import AIFeedback from './ai_feedback';
import FlagNodeModal from 'common/flag-modal';
import FlagEdgeModal from 'common/flag-modal/edge-flag-modal';
import {
  selectSuggestedEdges,
  selectSuggestedNodes,
  selectSelectedSuggestedEdge,
  selectConfirmationResult,
} from 'store/graph/selectors';
import { setConfirmationResult } from 'store/graph/actions';
import SuggestedEdgeDetailPanel from 'common/suggested-edge-detail';
import ConfirmRelationshipModal from 'common/confirm-relationship-modal';

const Home = () => {
  const dispatch = useDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const rootRef = useRef(null);
  const [showChat, setShowChat] = useState(false);
  const showFeedbackModal = useSelector(selectShowFeedbackModal);
  const isLoading = useSelector(selectIsLoading);
  const { thresholdExceeded } = useSearchNotification(isLoading);
  const showIndividualFilters = useSelector(selectShowIndividualFilters);
  const hasAiContext = useSelector(selectHasAiContext);
  const suggestedNodes = useSelector(selectSuggestedNodes);
  const suggestedEdges = useSelector(selectSuggestedEdges);
  const selectedSuggestedEdge = useSelector(selectSelectedSuggestedEdge);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const confirmationResult = useSelector(selectConfirmationResult);
  const [toast, setToast] = useState(null);

  // Show toast when confirmationResult changes
  useEffect(() => {
    if (confirmationResult) {
      setToast(confirmationResult);
      // Close confirm modal on success
      if (confirmationResult.status === 'success') {
        setShowConfirmModal(false);
      }
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setToast(null);
        dispatch(setConfirmationResult(null));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [confirmationResult, dispatch]);

  useEffect(() => {
    // Debug logs removed for selectedSuggestedEdge changes
  }, [selectedSuggestedEdge]);

  useEffect(() => {
    // Debug log removed for showConfirmModal changes
  }, [showConfirmModal]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === '/') {
        e.preventDefault();
        setShowChat((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div ref={rootRef} className={styles.root}>
      <Sidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        rootRef={rootRef}
      />
      <div
        className={`${styles.right_section} ${
          isCollapsed && styles.full_width
        }`}
        style={{
          width: isCollapsed ? '100%' : 'calc(100% - 300px)',
        }}
      >
        <div className={styles.filter_card_wrapper}>
          <FilterCards />
        </div>
        <SelectedEdge />
        <SelectedDetailNode />
        {isLoading ? (
          <Loading thresholdExceeded={thresholdExceeded} />
        ) : (
          <div style={{ height: '100%', marginRight: selectedSuggestedEdge ? '360px' : undefined }}>
            <ChartWrapper
              isCollapsed={isCollapsed}
              suggestedNodes={suggestedNodes}
              suggestedEdges={suggestedEdges}
            />
          </div>
        )}
        {showChat && (
          <CommandBar onClose={() => setShowChat(false)} />
        )}
        {hasAiContext && <AIFeedback />}
      </div>
      {selectedSuggestedEdge && (
        <SuggestedEdgeDetailPanel
          edgeKey={selectedSuggestedEdge}
          onConfirm={() => setShowConfirmModal(true)}
        />
      )}
      {showConfirmModal && (
        <ConfirmRelationshipModal
          edgeKey={selectedSuggestedEdge}
          onClose={() => setShowConfirmModal(false)}
        />
      )}
      {showFeedbackModal && <FeedbackModel />}
      {showIndividualFilters && <ContextFiltersModal />}
      <FlagNodeModal />
      <FlagEdgeModal />
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            borderRadius: 8,
            color: '#fff',
            backgroundColor:
              toast.status === 'success'
                ? '#2e7d32'
                : toast.status === 'info'
                ? '#1565c0'
                : '#d32f2f',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            zIndex: 10000,
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
          role="status"
          aria-live="polite"
        >
          {toast.status === 'success' ? '✓' : toast.status === 'info' ? '…' : '✗'} {toast.message}
          <button
            onClick={() => { setToast(null); dispatch(setConfirmationResult(null)); }}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, marginLeft: 8 }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default Home;
